import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Clock, LogOut, RefreshCw } from 'lucide-react';
import api from '@/lib/api';

const WARNING_BEFORE_MS = 5 * 60 * 1000; // Warn 5 minutes before expiry
const CHECK_INTERVAL_MS = 30 * 1000;      // Check every 30 seconds

function decodeTokenPayload(token) {
  try {
    const base64 = token.split('.')[1];
    const json = atob(base64.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export default function SessionExpiryWarning() {
  const { user, logout, setUser } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [extending, setExtending] = useState(false);
  const timerRef = useRef(null);
  const countdownRef = useRef(null);

  const getExpiryMs = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const payload = decodeTokenPayload(token);
    if (!payload?.exp) return null;
    return payload.exp * 1000; // Convert to ms
  }, []);

  const checkExpiry = useCallback(() => {
    if (!user) return;
    const expiryMs = getExpiryMs();
    if (!expiryMs) return;

    const now = Date.now();
    const timeLeft = expiryMs - now;

    if (timeLeft <= 0) {
      // Already expired — logout
      logout();
      return;
    }

    if (timeLeft <= WARNING_BEFORE_MS && !showWarning) {
      setShowWarning(true);
      setRemainingSeconds(Math.floor(timeLeft / 1000));

      // Start countdown
      if (countdownRef.current) clearInterval(countdownRef.current);
      countdownRef.current = setInterval(() => {
        const nowMs = Date.now();
        const left = expiryMs - nowMs;
        if (left <= 0) {
          clearInterval(countdownRef.current);
          logout();
          return;
        }
        setRemainingSeconds(Math.floor(left / 1000));
      }, 1000);
    }
  }, [user, getExpiryMs, showWarning, logout]);

  useEffect(() => {
    if (!user) return;
    checkExpiry();
    timerRef.current = setInterval(checkExpiry, CHECK_INTERVAL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [user, checkExpiry]);

  const handleExtend = async () => {
    setExtending(true);
    try {
      const res = await api.post('/auth/refresh');
      const { token, user: userData } = res.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      setShowWarning(false);
      if (countdownRef.current) clearInterval(countdownRef.current);
    } catch {
      // If refresh fails, force logout
      logout();
    } finally {
      setExtending(false);
    }
  };

  const handleLogout = () => {
    setShowWarning(false);
    if (countdownRef.current) clearInterval(countdownRef.current);
    logout();
  };

  const formatTime = (totalSeconds) => {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  if (!user || !showWarning) return null;

  return (
    <Dialog open={showWarning} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="session-expiry-dialog"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-500" />
            Session Expiring Soon
          </DialogTitle>
          <DialogDescription>
            Your session will expire in{' '}
            <span className="font-mono font-semibold text-amber-600">{formatTime(remainingSeconds)}</span>.
            Would you like to extend it?
          </DialogDescription>
        </DialogHeader>

        {/* Progress bar */}
        <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
          <div
            className="h-full bg-amber-500 transition-all duration-1000 ease-linear rounded-full"
            style={{ width: `${Math.max(0, (remainingSeconds / 300) * 100)}%` }}
          />
        </div>

        <div className="flex gap-3 pt-2">
          <Button
            onClick={handleExtend}
            disabled={extending}
            className="flex-1 bg-primary hover:bg-primary/90 text-white gap-2"
            data-testid="session-extend-btn"
          >
            <RefreshCw className={`w-4 h-4 ${extending ? 'animate-spin' : ''}`} />
            {extending ? 'Extending...' : 'Extend Session'}
          </Button>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="gap-2"
            data-testid="session-logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
